<template>
  <header class="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 transition-all">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center py-3.5">
        <div class="flex-1 flex items-center justify-start">
          <a href="/" class="flex items-center gap-2 no-underline text-gray-900">
            <span class="text-2xl text-indigo-600">✦</span>
            <span class="font-bold text-xl tracking-tight">АртМандала</span>
          </a>
        </div>

        <!-- Desktop Nav items -->
        <nav class="hidden md:flex flex-1 items-center justify-center gap-7">
          <a v-for="item in navigation" :key="item.name" :href="item.href" 
             class="font-medium text-[0.95rem] transition-colors"
             :class="isActive(item.href) ? 'text-indigo-600' : 'text-gray-500 hover:text-indigo-600'">
            {{ item.name }}
          </a>

          <!-- Interactive Dropdown Menu -->
          <div class="relative group mt-1 -translate-y-0.5">
            <button class="font-medium text-[0.95rem] text-gray-500 hover:text-indigo-600 transition-colors flex items-center gap-1 cursor-pointer" :class="{ 'text-indigo-600': isActive('/zelaniya') }">
              Намерения <span class="text-[0.6rem] opacity-70 mt-0.5">▼</span>
            </button>
            <div class="absolute left-1/2 -translate-x-1/2 top-[calc(100%-4px)] pt-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div class="bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-64 flex flex-col items-start overflow-hidden">
                <a href="/zelaniya/dengi" class="w-full text-left px-5 py-2.5 text-[0.95rem] text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors" :class="{ 'bg-indigo-50 text-indigo-600': isActive('/zelaniya/dengi') }">Деньги и Изобилие</a>
                <a href="/zelaniya/zdorove" class="w-full text-left px-5 py-2.5 text-[0.95rem] text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors" :class="{ 'bg-indigo-50 text-indigo-600': isActive('/zelaniya/zdorove') }">Здоровье и Энергия</a>
                <a href="/zelaniya/lubov" class="w-full text-left px-5 py-2.5 text-[0.95rem] text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors" :class="{ 'bg-indigo-50 text-indigo-600': isActive('/zelaniya/lubov') }">Любовь и Отношения</a>
                <div class="w-full h-px bg-gray-100 my-1"></div>
                <a href="/zelaniya/" class="w-full text-left px-5 py-2.5 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors">Смотреть все запросы →</a>
              </div>
            </div>
          </div>
        </nav>

        <!-- Desktop Auth actions -->
        <div class="hidden md:flex flex-1 items-center justify-end gap-6">
          <template v-if="isAuthenticated">
            <button @click="logout" class="text-sm font-semibold text-gray-500 hover:text-indigo-600 transition-colors">
              Выйти
            </button>
            <router-link to="/profile" class="text-gray-500 font-medium text-[0.95rem] hover:text-indigo-600 transition-colors">
              Личный кабинет
            </router-link>
            <router-link to="/app" class="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              Приложение
            </router-link>
          </template>
          <template v-else>
            <router-link to="/app" class="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm">
              Приложение
            </router-link>
          </template>
        </div>

        <!-- Mobile: App button + menu button -->
        <div class="flex md:hidden items-center gap-2">
          <router-link to="/app" class="px-3 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm whitespace-nowrap">
            Приложение
          </router-link>
          <button
            type="button"
            class="bg-white border-2 border-gray-300 rounded-lg p-2 inline-flex items-center justify-center text-gray-800 hover:border-indigo-600 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            @click="toggleMobileMenu"
            aria-label="Меню"
          >
            <span class="sr-only">{{ isMobileMenuOpen ? 'Close menu' : 'Open menu' }}</span>
            <svg class="h-6 w-6" :class="{ 'hidden': isMobileMenuOpen, 'block': !isMobileMenuOpen }" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg class="h-6 w-6" :class="{ 'block': isMobileMenuOpen, 'hidden': !isMobileMenuOpen }" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu dropdown -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <div v-if="isMobileMenuOpen" class="absolute top-full left-0 right-0 max-h-screen overflow-y-auto bg-white border-t border-gray-100 shadow-xl md:hidden">
        <div class="px-4 pt-2 mb-6">
          <nav class="flex flex-col space-y-1">
            <a
              v-for="item in navigation"
              :key="item.name"
              :href="item.href"
              class="block px-3 py-3 text-base font-medium rounded-md transition-colors"
              :class="isActive(item.href) ? 'text-indigo-600 bg-indigo-50' : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-50'"
            >
              {{ item.name }}
            </a>

            <!-- Mobile Dropdown Flat Items -->
            <div class="pt-2 pb-1 border-t border-gray-100 mt-2">
              <p class="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Намерения</p>
              <a href="/zelaniya/dengi" class="block px-3 py-2.5 text-[0.95rem] font-medium rounded-md transition-colors" :class="isActive('/zelaniya/dengi') ? 'text-indigo-600 bg-indigo-50' : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-50'">Деньги и Изобилие</a>
              <a href="/zelaniya/zdorove" class="block px-3 py-2.5 text-[0.95rem] font-medium rounded-md transition-colors" :class="isActive('/zelaniya/zdorove') ? 'text-indigo-600 bg-indigo-50' : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-50'">Здоровье и Энергия</a>
              <a href="/zelaniya/lubov" class="block px-3 py-2.5 text-[0.95rem] font-medium rounded-md transition-colors" :class="isActive('/zelaniya/lubov') ? 'text-indigo-600 bg-indigo-50' : 'text-gray-500 hover:text-indigo-600 hover:bg-gray-50'">Любовь и Отношения</a>
              <a href="/zelaniya/" class="block px-3 py-2.5 text-sm font-semibold text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors mt-1">Все запросы →</a>
            </div>
          </nav>
          
          <div class="mt-6 pt-6 border-t border-gray-100">
            <div v-if="isAuthenticated" class="flex flex-col space-y-3">
              <router-link
                to="/profile"
                class="block px-3 py-3 text-base font-medium text-gray-600 hover:text-indigo-600 hover:bg-gray-50 rounded-md"
                @click="toggleMobileMenu"
              >
                Личный кабинет
              </router-link>
              <router-link
                to="/app"
                class="block px-3 py-3 text-base font-medium text-center text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm"
                @click="toggleMobileMenu"
              >
                Приложение
              </router-link>
              <button
                @click="logout"
                class="block w-full text-left px-3 py-3 text-base font-medium text-red-500 hover:bg-red-50 rounded-md"
              >
                Выйти
              </button>
            </div>
            <div v-else class="flex flex-col space-y-3">
              <router-link
                to="/app"
                class="block px-3 py-3 text-base font-medium text-center text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm"
                @click="toggleMobileMenu"
              >
                Приложение
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/user';
import { storeToRefs } from 'pinia';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);
const isMobileMenuOpen = ref(false);

const isActive = (href) => {
  if (!route) return false;
  const path = route.path;
  if (href.startsWith('/#')) return false;
  // Exact match for home, starts-with match for sub-routes
  return path === href || (href !== '/' && path.startsWith(href));
};

const logout = async () => {
  await authStore.logout();
  router.push('/signin');
};

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const navigation = [
  { name: 'Главная', href: '/' },
  { name: 'Наборы', href: '/nabory' },
  { name: 'Демо', href: '/democalc' },
  { name: 'Тарифы', href: '/#plans' },
  { name: 'Блог', href: '/blog/' },
];
</script>
